class DarkModeToggle {
  constructor() {
    this.storageKey = 'dokibird-theme-mode';
    this.darkModeClass = 'dark-mode';
    this.lightModeClass = 'light-mode';
    this.body = document.body;
    this.html = document.documentElement;
    
    this.init();
  }

  init() {
    // Get settings from theme
    this.enableDarkMode = window.theme?.settings?.enable_dark_mode || true;
    this.defaultMode = window.theme?.settings?.default_theme_mode || 'auto';
    
    if (!this.enableDarkMode) return;

    // Add improved header spacing CSS
    this.addHeaderSpacingCSS();

    // Create toggle button (theme is already set by head script)
    this.createToggleButton();
    
    // Ensure button state is correct with a small delay to handle timing issues
    setTimeout(() => {
      const currentTheme = this.getCurrentTheme();
      this.updateToggleButton(currentTheme);
    }, 50);
    
    // Listen for system preference changes
    this.watchSystemPreference();
    
    // Listen for storage changes (for sync across tabs)
    this.watchStorageChanges();
  }

  addHeaderSpacingCSS() {
    if (document.querySelector('#dark-mode-header-styles')) return;

    const style = document.createElement('style');
    style.id = 'dark-mode-header-styles';
    style.textContent = `
      /* Improved header spacing with dark mode toggle */
      .header__icons {
        gap: 0.4rem;
        padding-right: 0.8rem;
        align-items: center;
        justify-content: center;
        display: flex;
      }
      
      .header__icon {
        flex-shrink: 0;
        margin-left: 0;
        transition: all 0.2s ease;
        border-radius: 8px;
      }
      
      /* Theme-based icon colors */
      .light-mode .header__icon {
        color: #4F49A2;
      }
      
      .dark-mode .header__icon {
        color: #FEE96D;
      }
      
      .light-mode .dark-mode-toggle {
        color: #4F49A2;
      }
      
      .dark-mode .dark-mode-toggle {
        color: #FEE96D;
      }
      
      /* Improved hover effects for all header icons */
      .header__icon:hover {
        background-color: rgba(var(--color-foreground), 0.08);
        transform: none;
      }
      
      .header__icon:hover .icon {
        transform: scale(1.05);
      }
      
      /* Reset any existing margins that might cause uneven spacing */
      .header__icon--cart {
        margin-right: 0;
      }
      
      /* Improved menu hover effects */
      .header__menu-item {
        transition: all 0.2s ease;
        border-radius: 8px;
        position: relative;
      }
      
      .header__menu-item:hover {
        background-color: rgba(var(--color-foreground), 0.08);
        color: rgb(var(--color-foreground));
      }
      
      /* Remove the underline effect and replace with background */
      .header__menu-item:hover span {
        text-decoration: none;
      }
      
      /* Ensure logo doesn't get too small */
      .header__heading-logo-wrapper {
        min-width: 120px;
      }
      
      /* Enhanced responsive breakpoints for header layout */
      @media screen and (max-width: 749px) {
        .header {
          grid-template-columns: auto 1fr auto;
          padding-left: 1rem;
          padding-right: 1rem;
        }
        
        .header__icons {
          gap: 0.3rem;
          padding-right: 0.4rem;
        }
        
        .header__heading-logo-wrapper {
          min-width: 80px;
        }
      }
      
      @media screen and (min-width: 750px) and (max-width: 990px) {
        .header {
          grid-template-columns: auto 1fr auto;
          padding-left: 1.5rem;
          padding-right: 1.5rem;
        }
        
        .header__icons {
          gap: 0.4rem;
          padding-right: 0.6rem;
        }
        
        .header__heading-logo-wrapper {
          min-width: 100px;
        }
      }
      
      @media screen and (min-width: 991px) and (max-width: 1350px) {
        .header {
          grid-template-columns: auto 1fr auto;
          padding-left: 1.5rem;
          padding-right: 1.5rem;
        }
        
        .header__icons {
          gap: 0.4rem;
          padding-right: 0.8rem;
        }
      }
      
      @media screen and (min-width: 1351px) {
        .header {
          grid-template-columns: 1fr auto 1fr;
          padding-left: 2rem;
          padding-right: 2rem;
        }
        
        .header__icons {
          gap: 0.5rem;
          padding-right: 1rem;
        }
      }
      
      /* Specific adjustments for menus with many items */
      .header--has-menu .header__icons {
        margin-left: auto;
      }
      
      /* Dark mode toggle specific styles */
      .dark-mode-toggle {
        flex-shrink: 0;
        transition: all 0.2s ease;
        border-radius: 8px;
        background-color: transparent;
      }
      
      .dark-mode-toggle:hover {
        background-color: rgba(var(--color-foreground), 0.08);
        transform: none;
      }
      
      .dark-mode-toggle:hover .icon {
        transform: scale(1.05);
      }
      
      .dark-mode-toggle .icon {
        width: 2rem;
        height: 2rem;
        transition: transform 0.2s ease;
      }
    `;
    document.head.appendChild(style);
  }

  setInitialTheme() {
    const savedTheme = localStorage.getItem(this.storageKey);
    
    if (savedTheme) {
      this.applyTheme(savedTheme);
    } else {
      // Use default setting
      switch (this.defaultMode) {
        case 'dark':
          this.applyTheme('dark');
          break;
        case 'light':
          this.applyTheme('light');
          break;
        case 'auto':
        default:
          this.applyTheme(this.getSystemPreference());
          break;
      }
    }
  }

  getSystemPreference() {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  getCurrentTheme() {
    return this.html.classList.contains(this.darkModeClass) ? 'dark' : 'light';
  }

  applyTheme(theme) {
    // Remove existing theme classes from both html and body
    this.body.classList.remove(this.darkModeClass, this.lightModeClass);
    this.html.classList.remove(this.darkModeClass, this.lightModeClass);
    
    // Add new theme class to both elements
    if (theme === 'dark') {
      this.html.classList.add(this.darkModeClass);
      this.body.classList.add(this.darkModeClass);
    } else {
      this.html.classList.add(this.lightModeClass);
      this.body.classList.add(this.lightModeClass);
    }

    // Update meta theme color for mobile browsers
    this.updateMetaThemeColor(theme);
    
    // Update logo visibility
    this.updateLogoVisibility(theme);
    
    // Dispatch custom event for other components
    this.dispatchThemeChangeEvent(theme);
    
    // Update toggle button state
    this.updateToggleButton(theme);
  }

  updateMetaThemeColor(theme) {
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      const color = theme === 'dark' ? '#121212' : '#ffffff';
      metaThemeColor.setAttribute('content', color);
    }
  }

  updateLogoVisibility(theme) {
    // Logo visibility is now handled entirely by CSS
    // This method exists for potential future enhancements
    // or custom logic if needed
    
    // Dispatch logo change event for other components that might need it
    const logoChangeEvent = new CustomEvent('logochange', {
      detail: { theme },
      bubbles: true
    });
    document.dispatchEvent(logoChangeEvent);
  }

  toggleTheme() {
    const currentTheme = this.getCurrentTheme();
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    
    this.applyTheme(newTheme);
    localStorage.setItem(this.storageKey, newTheme);
  }

  createToggleButton() {
    // Check if button already exists
    if (document.querySelector('.dark-mode-toggle')) return;

    const toggleButton = document.createElement('button');
    toggleButton.className = 'dark-mode-toggle header__icon';
    toggleButton.setAttribute('aria-label', 'Toggle dark mode');
    toggleButton.innerHTML = `
      <svg class="icon icon-sun" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
      <svg class="icon icon-moon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>
    `;

    // Add click event
    toggleButton.addEventListener('click', () => this.toggleTheme());

    // Insert after the cart icon
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (cartIcon && cartIcon.parentNode) {
      cartIcon.parentNode.insertBefore(toggleButton, cartIcon.nextSibling);
    } else {
      // Fallback: insert into header icons as last child
      const headerIcons = document.querySelector('.header__icons');
      if (headerIcons) {
        headerIcons.appendChild(toggleButton);
      }
    }

    // Set initial icon state immediately after creation
    const currentTheme = this.getCurrentTheme();
    this.updateToggleButton(currentTheme);
  }

  updateToggleButton(theme) {
    const button = document.querySelector('.dark-mode-toggle');
    if (!button) return;

    const sunIcon = button.querySelector('.icon-sun');
    const moonIcon = button.querySelector('.icon-moon');

    if (theme === 'dark') {
      sunIcon.style.display = 'block';
      moonIcon.style.display = 'none';
      button.setAttribute('aria-label', 'Switch to light mode');
    } else {
      sunIcon.style.display = 'none';
      moonIcon.style.display = 'block';
      button.setAttribute('aria-label', 'Switch to dark mode');
    }
  }

  watchSystemPreference() {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const savedTheme = localStorage.getItem(this.storageKey);
      if (!savedTheme && this.defaultMode === 'auto') {
        this.applyTheme(e.matches ? 'dark' : 'light');
      }
    });
  }

  watchStorageChanges() {
    window.addEventListener('storage', (e) => {
      if (e.key === this.storageKey && e.newValue) {
        this.applyTheme(e.newValue);
      }
    });
  }

  dispatchThemeChangeEvent(theme) {
    const event = new CustomEvent('themechange', {
      detail: { theme },
      bubbles: true
    });
    document.dispatchEvent(event);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new DarkModeToggle();
  });
} else {
  new DarkModeToggle();
} 